import type { Gig, Position, Call } from '@gigdriver/domain';
import { getOrgGigs, getGigPositions, getGigCalls } from './gigs';

export interface OpenPositionListing {
  gig: Gig;
  position: Position;
  // The position's own linked calls, resolved — empty for a call-less
  // (day/venue/role-only) position.
  calls: Call[];
}

// Every OPEN position across the org's non-cancelled/complete gigs —
// composed client-side from the same isOrgMember-gated gigs/positions/calls
// reads a `staff` member already has, rather than a collectionGroup query
// (which would need its own top-level rule + index). Fine at today's scale;
// an org with many gigs may eventually want a denormalized index instead.
export async function getOpenPositions(orgId: string): Promise<OpenPositionListing[]> {
  const gigs = (await getOrgGigs(orgId)).filter(g => g.state !== 'CANCELLED' && g.state !== 'COMPLETED');

  const perGig = await Promise.all(
    gigs.map(async gig => {
      const [positions, calls] = await Promise.all([getGigPositions(orgId, gig.id), getGigCalls(orgId, gig.id)]);
      const callById = new Map(calls.map(c => [c.id, c]));
      return positions
        .filter(position => position.state === 'OPEN')
        .map(
          (position): OpenPositionListing => ({
            gig,
            position,
            calls: position.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c)),
          }),
        );
    }),
  );

  return perGig.flat();
}
